import { atom, Atom, createStore, PrimitiveAtom, useAtomValue } from 'jotai';
import { IdentifiableAttribute, MutableAttribute } from './Attribute';
import { AttributeStore, Updater } from './AttributeStore';

/**
 * Type of any jotai store.
 * @see https://jotai.org/docs/core/store .
 */
export type JotaiStore = ReturnType<typeof createStore>;

export type JotaiAttributeStoreId = Atom<unknown>;
export type JotaiAttributeStoreGetter = <Type>(
  attribute: IdentifiableAttribute<Type, JotaiAttributeStoreId>,
) => Type;
export type JotaiAttributeStoreDerive<Type> = (get: JotaiAttributeStoreGetter) => Type;

/**
 * An implementation of {@link AttributeStore} using jotai.
 */
export class JotaiAttributeStore extends AttributeStore<JotaiAttributeStoreId> {
  private _jotaiStore: JotaiStore;

  constructor(args: { jotaiStore: JotaiStore }) {
    super();
    this._jotaiStore = args.jotaiStore;
  }

  mutableId<Type>(initialValue: Type): JotaiAttributeStoreId {
    return atom(initialValue);
  }

  derivableId<Type>(derive: JotaiAttributeStoreDerive<Type>): JotaiAttributeStoreId {
    return atom(get => {
      const getter = <U>(attribute: IdentifiableAttribute<U, JotaiAttributeStoreId>): U => {
        return get(attribute._id) as U;
      };

      return derive(getter);
    });
  }

  get<Type>(attributeId: Atom<unknown>): Type {
    return this._jotaiStore.get(attributeId as Atom<Type>);
  }

  set<Type>(attributeId: Atom<unknown>, nextValueOrUpdater: Type | Updater<Type>): void {
    this._jotaiStore.set(attributeId as PrimitiveAtom<Type>, nextValueOrUpdater);
  }

  mutable<Type>(initialValue: Type): MutableAttribute<Type, Atom<unknown>> {
    return new MutableAttribute({ store: this, rawValue: initialValue });
  }

  useAttributeValue<Type>(attributeId: Atom<unknown>): Type {
    return useAtomValue(attributeId as PrimitiveAtom<Type>);
  }
}
